import { FontSize } from "config/font";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { EyeIcon } from "../textInput/textInput.styled";
import { Eye } from "../textInput/textInput.component";
import {
  DropdownContainer,
  DropdownLabel,
  DropdownSelect,
} from "../dropDownWithLabel/dropDownWithLabel.styled";

interface TimePickerWithLabelProps {
  label: string;
  value?: string;
  defaultDate?: string;
  required?: boolean;
  hasError?: boolean;
  fontSize?: FontSize;
  placeholder?: string;
  disabled?: boolean;
  onChange?: (value: string) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  minuteStep?: number;
  inputSize?: "medium" | "small";
  width?: number;
  marginTop?: number;
  marginBottom?: number;
  marginLeft?: number;
  marginRight?: number;
}

const pad = (n: number) => (n < 10 ? `0${n}` : String(n));
const formatTime = (h: number, m: number) => `${pad(h)}:${pad(m)}`;

const parseTime = (s?: string): { h: number; m: number } | null => {
  if (!s) return null;
  const m = s.match(/^([0-9]{1,2}):([0-9]{2})$/);
  if (!m) return null;
  const h = Number(m[1]);
  const mi = Number(m[2]);
  if (Number.isNaN(h) || Number.isNaN(mi)) return null;
  return { h, m: mi };
};

const extractTimeFromISO = (isoString?: string): string => {
  if (!isoString) return "";
  try {
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return "";
    return formatTime(date.getHours(), date.getMinutes());
  } catch {
    return "";
  }
};

const extractDateFromISO = (isoString?: string, defaultDate?: string): Date => {
  if (isoString) {
    try {
      const date = new Date(isoString);
      if (!isNaN(date.getTime())) return date;
    } catch {}
  }
  if (defaultDate) {
    try {
      const date = new Date(defaultDate);
      if (!isNaN(date.getTime())) return date;
    } catch {}
  }
  return new Date();
};

const combineToISO = (baseDate: Date, timeString: string): string => {
  const time = parseTime(timeString);
  if (!time) return "";
  const combined = new Date(baseDate);
  combined.setHours(time.h, time.m, 0, 0);
  return combined.toISOString();
};

const isDSTGapDay = (): boolean => {
  const now = new Date();
  const lastOfMarch = new Date(now.getFullYear(), 3, 0);
  const lastSundayDate = lastOfMarch.getDate() - lastOfMarch.getDay();
  return now.getMonth() === 2 && now.getDate() === lastSundayDate;
};

export const TimePickerWithLabel: React.FC<TimePickerWithLabelProps> = ({
  label,
  value,
  defaultDate,
  required,
  hasError,
  fontSize,
  placeholder = "Välj tid",
  disabled,
  onChange,
  onBlur,
  onFocus,
  minuteStep = 1,
  inputSize = "medium",
  width,
  marginBottom,
  marginTop,
  marginLeft,
  marginRight,
}) => {
  const DST_GAP_DAY = useMemo(isDSTGapDay, []);
  const sizeVariant = inputSize ?? "medium";

  const [isFocused, setIsFocused] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement>(null);
  const overlayInputRef = useRef<HTMLInputElement>(null);
  const listRefHours = useRef<HTMLDivElement>(null);
  const listRefMinutes = useRef<HTMLDivElement>(null);

  const [currentDate, setCurrentDate] = useState<Date>(() =>
    extractDateFromISO(value, defaultDate)
  );

  const [committedValue, setCommittedValue] = useState<string>(() =>
    extractTimeFromISO(value)
  );

  const [editingValue, setEditingValue] = useState<string>("");

  const lastValidValue = useRef<string | undefined>(extractTimeFromISO(value));

  const closePanel = (doBlur = true) => {
    setIsOpen(false);
    setIsFocused(false);
    if (doBlur) overlayInputRef.current?.blur();
    onBlur?.();
  };

  const isEditing = isFocused;
  useEffect(() => {
    const newTime = extractTimeFromISO(value);
    const newDate = extractDateFromISO(value, defaultDate);

    if (DST_GAP_DAY && /^02:[0-5][0-9]$/.test(newTime)) {
      setCommittedValue("03:00");
      lastValidValue.current = "03:00";
      setCurrentDate(newDate);
      onChange?.(combineToISO(newDate, "03:00"));
      return;
    }
    setCommittedValue(newTime);
    setCurrentDate(newDate);
    lastValidValue.current = newTime;
  }, [value, defaultDate, DST_GAP_DAY, onChange]);

  const hourOptions = useMemo(() => {
    const arr: number[] = [];
    for (let i = 0; i < 24; i++) {
      if (DST_GAP_DAY && i === 2) continue;
      arr.push(i);
    }
    return arr;
  }, [DST_GAP_DAY]);

  const minuteOptions = useMemo(() => {
    const step = Math.max(1, minuteStep);
    const arr: number[] = [];
    for (let i = 0; i < 60; i += step) arr.push(i);
    return arr;
  }, [minuteStep]);

  const commit = (h: number, m: number) => {
    if (DST_GAP_DAY && h === 2) {
      h = 3;
      m = 0;
    }
    if (h < 0) h = 0;
    if (h > 23) h = 23;
    if (m < 0) m = 0;
    if (m > 59) m = 59;

    const timeStr = formatTime(h, m);
    const iso = combineToISO(currentDate, timeStr);
    setCommittedValue(timeStr);
    lastValidValue.current = timeStr;
    onChange?.(iso);
  };

  const openAndFocus = () => {
    if (disabled) return;
    setIsFocused(true);
    onFocus?.();
    setIsOpen(true);
    requestAnimationFrame(() => {
      const hs = listRefHours.current?.querySelector('[data-active="true"]');
      const ms = listRefMinutes.current?.querySelector('[data-active="true"]');
      (hs as HTMLElement | null)?.scrollIntoView({ block: "center" });
      (ms as HTMLElement | null)?.scrollIntoView({ block: "center" });
    });
  };

  const allowedPatternLoose = /^([0-9]{0,2})(:?)([0-9]{0,2})$/;

  const normalizeFreeInput = (raw: string): string | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;

    if (/^[0-9]{1,4}$/.test(trimmed)) {
      const s =
        trimmed.length <= 2
          ? trimmed.padStart(2, "0")
          : trimmed.padStart(4, "0").slice(-4);
      const hh = s.length <= 2 ? s : s.slice(0, 2);
      const mm = s.length <= 2 ? "00" : s.slice(2, 4);
      return `${hh}:${mm}`;
    }

    const m = trimmed.match(allowedPatternLoose);
    if (!m) return null;
    let hh = m[1] || "";
    const colon = m[2] || "";
    let mm = m[3] || "";

    if (colon) {
      if (hh.length === 1) hh = `0${hh}`;
      if (mm.length === 0) mm = "00";
      if (mm.length === 1) mm = `0${mm}`;
      return `${hh}:${mm}`;
    }

    if (!colon && (hh.length === 1 || hh.length === 2)) {
      if (hh.length === 1) hh = `0${hh}`;
      return `${hh}:00`;
    }

    return null;
  };

  const clampAndValidate = (
    candidate: string
  ): { h: number; m: number } | null => {
    const p = parseTime(candidate);
    if (!p) return null;
    let { h, m } = p;

    if (h < 0 || h > 23) return null;
    if (m < 0 || m > 59) return null;

    if (DST_GAP_DAY && h === 2) {
      h = 3;
      m = 0;
    }
    return { h, m };
  };

  const tryCommitFreeInput = (raw: string): boolean => {
    const normalized = normalizeFreeInput(raw);
    if (!normalized) return false;
    const valid = clampAndValidate(normalized);
    if (!valid) return false;
    commit(valid.h, valid.m);
    return true;
  };

  const validateAndCommitOnBlur = () => {
    const raw = editingValue;

    if (raw.trim() === "") {
      setEditingValue("");
      return;
    }

    if (tryCommitFreeInput(raw)) {
      setEditingValue("");
      return;
    }

    setEditingValue("");
    setCommittedValue(lastValidValue.current || "");
  };

  useEffect(() => {
    const onDocDown = (e: MouseEvent) => {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        onBlur?.();
        setIsFocused(false);
        validateAndCommitOnBlur();
      }
    };
    document.addEventListener("mousedown", onDocDown);
    return () => document.removeEventListener("mousedown", onDocDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onBlur, editingValue, currentDate]);

  const onOverlayChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    if (disabled) return;
    const raw = e.target.value.replace(/[^\d:]/g, "");

    if (/^\d{4}$/.test(raw)) {
      const hh = raw.slice(0, 2);
      const mm = raw.slice(2, 4);
      setEditingValue(`${hh}:${mm}`);
      return;
    }

    if (allowedPatternLoose.test(raw)) setEditingValue(raw);
  };

  const onOverlayFocus: React.FocusEventHandler<HTMLInputElement> = () => {
    if (disabled) return;
    setIsFocused(true);
    onFocus?.();
    setEditingValue("");
    if (!isOpen) openAndFocus();
  };

  const onOverlayBlur: React.FocusEventHandler<HTMLInputElement> = () => {
    setIsFocused(false);
    onBlur?.();
    validateAndCommitOnBlur();
  };

  const onOverlayKeyDown: React.KeyboardEventHandler<HTMLInputElement> = (
    e
  ) => {
    if (disabled) return;

    if (e.key === "Enter") {
      e.preventDefault();
      if (!tryCommitFreeInput(editingValue)) {
        setCommittedValue(lastValidValue.current || "");
      }
      setEditingValue("");
      setIsOpen(false);
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
      setEditingValue("");
      setCommittedValue(lastValidValue.current || "");
      (e.currentTarget as HTMLInputElement).blur();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) openAndFocus();
      return;
    }
  };

  const displayText =
    (isEditing ? editingValue : committedValue) || placeholder || "Välj tid";

  const panelStyle: React.CSSProperties = {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: 6,
    width: 220,
    background: "white",
    borderRadius: 8,
    boxShadow: "0 8px 20px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.08)",
    padding: 8,
    zIndex: 9999,
    userSelect: "none",
  };

  const colStyle: React.CSSProperties = {
    flex: 1,
    maxHeight: 220,
    overflowY: "auto",
    padding: 4,
  };

  const itemBase: React.CSSProperties = {
    width: "100%",
    textAlign: "left",
    padding: "6px 8px",
    borderRadius: 6,
    cursor: "pointer",
    font: "inherit",
    background: "transparent",
    outline: "none",
  };

  const itemStyle: React.CSSProperties = { ...itemBase };
  const activeStyle: React.CSSProperties = {
    ...itemBase,
    background: "#1976d2",
    color: "white",
    borderColor: "#0d5cab",
  };
  const hoverStyle: React.CSSProperties = {
    background: "rgba(25,118,210,0.08)",
  };

  const [hoverIndexH, setHoverIndexH] = useState<number | null>(null);
  const [hoverIndexM, setHoverIndexM] = useState<number | null>(null);

  const parsedDisplay = useMemo(
    () => parseTime(committedValue || "") || { h: 0, m: 0 },
    [committedValue]
  );
  let { h: hours, m: minutes } = parsedDisplay;

  const onKeyDownContainer: React.KeyboardEventHandler<HTMLDivElement> = (
    e
  ) => {
    if (!isOpen) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        openAndFocus();
      }
      return;
    }
    if (e.key === "Escape") {
      e.preventDefault();
      setIsOpen(false);
    }
  };

  return (
    <DropdownContainer
      ref={rootRef}
      className="time-picker-with-label"
      width={width}
      marginBottom={marginBottom}
      marginTop={marginTop}
      marginLeft={marginLeft}
      marginRight={marginRight}
    >
      <DropdownLabel
        required={required}
        hasValue={!!committedValue}
        isFocused={isFocused || isOpen}
        disabled={disabled}
        $size={sizeVariant}
      >
        {label}
      </DropdownLabel>

      <DropdownSelect
        as={"div" as any}
        hasError={hasError}
        fontSize={fontSize}
        hasValue={!!committedValue}
        isOpen={isOpen}
        disabled={disabled}
        $size={sizeVariant}
        onClick={(e: any) => {
          if (disabled) return;
          overlayInputRef.current?.focus();
          if (!isOpen) openAndFocus();
        }}
        onKeyDown={onKeyDownContainer}
        tabIndex={disabled ? -1 : 0}
        role="button"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
          width: "100%",
          cursor: disabled ? "not-allowed" : "pointer",
          userSelect: "none",
        }}
      >
        <span style={{ opacity: committedValue || isEditing ? 1 : 0.6 }}>
          {displayText}
        </span>

        {disabled ? (
          <EyeIcon>
            <Eye size={sizeVariant === "small" ? 12 : 16} />
          </EyeIcon>
        ) : null}

        <input
          ref={overlayInputRef}
          type="text"
          inputMode="numeric"
          value={editingValue}
          placeholder=""
          disabled={disabled}
          onChange={onOverlayChange}
          onFocus={onOverlayFocus}
          onBlur={onOverlayBlur}
          onKeyDown={onOverlayKeyDown}
          aria-label={label}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            background: "transparent",
            border: "none",
            outline: "none",
            padding: 0,
            margin: 0,
            font: "inherit",
            color: "transparent",
            caretColor: "auto",
            pointerEvents: disabled ? "none" : "auto",
          }}
        />

        {isOpen && !disabled && (
          <div
            style={panelStyle}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", gap: 8 }}>
              <div
                ref={listRefHours}
                style={colStyle}
                aria-label="Timmar"
                role="listbox"
              >
                {hourOptions.map((h, idx) => {
                  const active = h === hours;
                  return (
                    <div
                      key={h}
                      role="option"
                      aria-selected={active}
                      data-active={active ? "true" : undefined}
                      style={{
                        ...(active ? activeStyle : itemStyle),
                        ...(hoverIndexH === idx && !active ? hoverStyle : null),
                      }}
                      onMouseEnter={() => setHoverIndexH(idx)}
                      onMouseLeave={() => setHoverIndexH(null)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        const m = minutes;
                        commit(h, m);
                      }}
                    >
                      {pad(h)}
                    </div>
                  );
                })}
              </div>

              <div
                ref={listRefMinutes}
                style={colStyle}
                aria-label="Minuter"
                role="listbox"
              >
                {minuteOptions.map((m, idx) => {
                  const active = m === minutes;
                  return (
                    <div
                      key={m}
                      role="option"
                      aria-selected={active}
                      data-active={active ? "true" : undefined}
                      style={{
                        ...(active ? activeStyle : itemStyle),
                        ...(hoverIndexM === idx && !active ? hoverStyle : null),
                      }}
                      onMouseEnter={() => setHoverIndexM(idx)}
                      onMouseLeave={() => setHoverIndexM(null)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        commit(hours, m);
                        setEditingValue("");
                        closePanel();
                      }}
                    >
                      {pad(m)}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </DropdownSelect>
    </DropdownContainer>
  );
};
